import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { Workflow, Plus, Loader2, Trash2, Pencil, Power, PowerOff, Boxes, Search, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const inputCls = "w-full border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
const primaryBtn = "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2 disabled:opacity-60";
const secondaryBtn = "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2";

function StatTile({ icon: Icon, value, label, tone }) {
  const tones = {
    orange: "bg-orange-500/10 text-orange-500",
    green: "bg-green-500/10 text-green-400",
    slate: "bg-white/10 text-slate-400",
    sky: "bg-sky-500/10 text-sky-400",
  };
  return (
    <div className="bg-[#141414] border border-white/10 hover:border-white/20 rounded-lg shadow-sm p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${tones[tone] || tones.orange}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xl font-black text-white leading-none">{value}</p>
        <p className="text-[10px] tracking-[0.1em] uppercase font-bold text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function FlowsList() {
  const { activeBranchId, branches } = useBranch();
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [toCopy, setToCopy] = useState(null);
  const [copyTargetBranch, setCopyTargetBranch] = useState("");
  const [copying, setCopying] = useState(false);

  const load = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/flows", { params: { branch_id: activeBranchId } });
      setFlows(data);
    } finally {
      setLoading(false);
    }
  }, [activeBranchId]);

  useEffect(() => { load(); }, [load]);

  const createFlow = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/flows", { branch_id: activeBranchId, name: name.trim() || "Untitled Flow" });
      toast.success("Flow created");
      navigate(`/flows/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (f, val) => {
    try {
      await api.put(`/flows/${f.id}`, { is_active: val });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmCopy = async () => {
    if (!copyTargetBranch) return;
    setCopying(true);
    try {
      await api.post(`/flows/${toCopy.id}/duplicate`, { target_branch_id: copyTargetBranch });
      const branchName = branches.find((b) => b.id === copyTargetBranch)?.name || "the branch";
      toast.success(`Copied to ${branchName} — it's off by default, review it there before turning it on`);
      setToCopy(null);
      setCopyTargetBranch("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCopying(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/flows/${toDelete.id}`);
      toast.success("Flow deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setToDelete(null);
    }
  };

  const totalFlows = flows.length;
  const activeFlows = flows.filter((f) => f.is_active).length;
  const inactiveFlows = totalFlows - activeFlows;
  const totalNodes = flows.reduce((sum, f) => sum + (f.nodes || []).length, 0);
  const filteredFlows = flows.filter(
    (f) => !search.trim() || (f.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <div data-testid="flows-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-black text-white">Flow Builder</h1>
          <p className="text-sm text-slate-500 mt-2">
            Build automations visually: a keyword triggers a chain of messages and tappable buttons.
          </p>
        </div>
        <button data-testid="create-flow-button" className={primaryBtn} onClick={() => { setName(""); setOpen(true); }}>
          <Plus className="h-4 w-4" /> Create Flow
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile icon={Workflow} value={totalFlows} label="Total Flows" tone="orange" />
        <StatTile icon={Power} value={activeFlows} label="Active" tone="green" />
        <StatTile icon={PowerOff} value={inactiveFlows} label="Inactive" tone="slate" />
        <StatTile icon={Boxes} value={totalNodes} label="Nodes" tone="sky" />
      </div>

      <div className="relative max-w-sm mb-5">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="flows-search-input"
          className={`${inputCls} pl-9`}
          placeholder="Search flows…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden bg-[#141414]">
        <Table>
          <TableHeader>
            <TableRow className="bg-white/5">
              {["Name", "Trigger Keywords", "Blocks", "On", ""].map((h, i) => (
                <TableHead key={i} className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
            ) : filteredFlows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-slate-400">
                <Workflow className="h-6 w-6 mx-auto mb-2 text-slate-600" />
                {flows.length === 0 ? "No flows yet. Create one to start building an automation." : "No flows match your search."}
              </TableCell></TableRow>
            ) : filteredFlows.map((f) => (
              <TableRow key={f.id} data-testid={`flow-row-${f.id}`} className="cursor-pointer" onClick={() => navigate(`/flows/${f.id}`)}>
                <TableCell className="font-semibold text-white">{f.name}</TableCell>
                <TableCell className="text-sm text-slate-300">
                  {(f.trigger_keywords || []).length === 0
                    ? <span className="text-slate-400 italic">not set</span>
                    : f.trigger_keywords.join(", ")}
                </TableCell>
                <TableCell className="text-sm text-slate-300">{(f.nodes || []).length}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Switch data-testid={`flow-active-${f.id}`} checked={!!f.is_active} onCheckedChange={(v) => toggleActive(f, v)} />
                </TableCell>
                <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`edit-flow-${f.id}`} onClick={() => navigate(`/flows/${f.id}`)} className="p-2 rounded-md text-slate-500 hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`copy-flow-${f.id}`} onClick={() => { setCopyTargetBranch(""); setToCopy(f); }} className="p-2 rounded-md text-slate-500 hover:bg-white/10"><Copy className="h-4 w-4" /></button>
                  <button data-testid={`delete-flow-${f.id}`} onClick={() => setToDelete(f)} className="p-2 rounded-md text-red-500 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-flow-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Create Flow</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Flow Name</label>
            <input data-testid="flow-name-input" autoFocus className={`mt-1.5 ${inputCls}`} value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="e.g. Welcome Chat"
                  onKeyDown={(e) => e.key === "Enter" && createFlow()} />
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setOpen(false)}>Cancel</button>
            <button data-testid="save-flow-name-button" className={primaryBtn} disabled={creating} onClick={createFlow}>
              {creating && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toCopy} onOpenChange={(o) => !o && setToCopy(null)}>
        <DialogContent data-testid="copy-flow-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Copy "{toCopy?.name}" to another branch</DialogTitle></DialogHeader>
          <div className="py-2">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Target Branch</label>
            <Select value={copyTargetBranch} onValueChange={setCopyTargetBranch}>
              <SelectTrigger data-testid="copy-target-branch-select" className="mt-1.5"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
              <SelectContent>
                {branches.filter((b) => b.id !== activeBranchId).map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-2">
              Creates a full copy of every block (buttons, images, links) on the target branch, switched off so you can adjust anything branch-specific before turning it on.
            </p>
          </div>
          <DialogFooter>
            <button className={secondaryBtn} onClick={() => setToCopy(null)}>Cancel</button>
            <button data-testid="confirm-copy-flow-button" className={primaryBtn} disabled={copying || !copyTargetBranch} onClick={confirmCopy}>
              {copying && <Loader2 className="h-4 w-4 animate-spin" />} Copy
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-flow-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This flow and its automation will be removed. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-flow" className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
